import { collection, doc, documentId, getDocFromServer, getDocs, limit, orderBy, query, startAfter, startAt, endAt, where } from 'firebase/firestore';
import React, { useContext, useEffect, useRef, useState } from 'react'
import { db } from '../../firebase-config';
import { ActionIcon, Button, Center, Container, Group, Loader, Paper, Stack, Text, TextInput, Title } from '@mantine/core';
import { Helmet } from 'react-helmet';
import { UsersTable } from '../../components/usersTable/UsersTable';
import styles from './users.module.css';
import { AppContext } from '../../App';
import { IconSearch, IconX } from '@tabler/icons-react';

const chunkArray = (items, size) => {
    const chunks = [];
    for (let index = 0; index < items.length; index += size) {
        chunks.push(items.slice(index, index + size));
    }

    return chunks;
};

const matchesSearch = (user, searchTerm) => {
    const value = searchTerm.toLowerCase();
    return [
        user.id,
        user.name,
        user.phone,
        user.email
    ].some((field) => String(field || '').toLowerCase().includes(value));
};

const uniqueSearchVariants = (searchTerm) => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return [];

    const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    return Array.from(new Set([trimmed, trimmed.toLowerCase(), capitalized]));
};

export const Users = () => {
    const [latestUsers, setLatestUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    var lastVisible = useRef(null);
    const [currentPage, setCurrentPage] = useState(1);
    const fetchLimit = 10;
    const [hasMore, setHasMore] = useState(true);
    const currentBrandUsers = useRef([]);
    const currentBrandUsersLoaded = useRef(false);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const { globalState } = useContext(AppContext);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearchTerm(searchInput.trim());
        }, 350);

        return () => window.clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        // Reset pagination when brand changes
        lastVisible.current = null;
        setHasMore(true);
        currentBrandUsers.current = [];
        currentBrandUsersLoaded.current = false;
        setLatestUsers([]);
        setCurrentPage(1);
        fetchLatestData(fetchLimit, lastVisible);
    }, [globalState.currentBrand, searchTerm]);

    // Fetch users based on current brand filter
    const fetchUsers = async (fetchLimit, lastVisible) => {
        if (searchTerm) {
            return await searchUsers(searchTerm);
        }

        if (globalState.currentBrand && globalState.currentBrand !== 'All') {
            return await fetchUsersByBrand(globalState.currentBrand, fetchLimit, lastVisible);
        } else {
            return await fetchAllUsers(fetchLimit, lastVisible);
        }
    };

    const fetchUsersByIds = async (userIds) => {
        if (userIds.length === 0) return [];

        const snapshots = await Promise.all(chunkArray(userIds, 10).map((userIdsChunk) => {
            return getDocs(query(collection(db, 'users'), where('__name__', 'in', userIdsChunk)));
        }));

        const usersById = new Map();
        snapshots.forEach((snapshot) => {
            snapshot.docs.forEach((userDoc) => {
                usersById.set(userDoc.id, { ...userDoc.data(), id: userDoc.id });
            });
        });

        return userIds.map((userId) => usersById.get(userId)).filter(Boolean);
    };

    const fetchBrandClientIds = async (brandId) => {
        if (!currentBrandUsersLoaded.current) {
            const clientsQuery = query(collection(db, 'brands', brandId, 'clients'));
            const clientsSnapshot = await getDocs(clientsQuery);
            currentBrandUsers.current = clientsSnapshot.docs.map(doc => doc.id);
            currentBrandUsersLoaded.current = true;
        }

        return currentBrandUsers.current;
    };

    // Fetch users that have stamps for a specific brand
    const fetchUsersByBrand = async (brandId, userFetchLimit, lastVisible) => {
        const allUserIds = await fetchBrandClientIds(brandId);
        if (allUserIds.length === 0) return { users: [], userIds: [], hasMore: false };

        // Handle pagination
        const startIndex = lastVisible.current || 0;
        const endIndex = startIndex + userFetchLimit + 1;
        const paginatedUserIds = allUserIds.slice(startIndex, endIndex);

        const hasMoreResults = paginatedUserIds.length > userFetchLimit;
        const userIdsToFetch = hasMoreResults ? paginatedUserIds.slice(0, -1) : paginatedUserIds;

        if (userIdsToFetch.length === 0) return { users: [], userIds: [], hasMore: false };

        // Store the last index for pagination
        lastVisible.current = hasMoreResults ? startIndex + userFetchLimit : null;

        const users = await fetchUsersByIds(userIdsToFetch);

        return {
            users,
            userIds: userIdsToFetch,
            hasMore: hasMoreResults
        };
    };

    // Fetch all users without brand filter
    const fetchAllUsers = async (fetchLimit, lastVisible) => {
        const usersQuery = !lastVisible.current
            ? query(collection(db, 'users'), orderBy('name'), limit(fetchLimit + 1))
            : query(collection(db, 'users'), orderBy('name'), startAfter(lastVisible.current), limit(fetchLimit + 1));

        const querySnapshot = await getDocs(usersQuery);
        const hasMoreResults = querySnapshot.docs.length > fetchLimit;
        const docsToUse = hasMoreResults ? querySnapshot.docs.slice(0, -1) : querySnapshot.docs;

        lastVisible.current = docsToUse.length > 0 ? docsToUse[docsToUse.length - 1] : null;
        const users = docsToUse.map(doc => ({ ...doc.data(), id: doc.id }));
        const userIds = users.map(user => user.id);

        return { users, userIds, hasMore: hasMoreResults };
    };

    const runPrefixUserQuery = async (field, value) => {
        if (!value) return [];

        const querySnapshot = await getDocs(query(
            collection(db, 'users'),
            orderBy(field),
            startAt(value),
            endAt(`${value}\uf8ff`),
            limit(25)
        ));

        return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    };

    const runIdPrefixUserQuery = async (value) => {
        if (!value || value.includes('/')) return [];

        const querySnapshot = await getDocs(query(
            collection(db, 'users'),
            orderBy(documentId()),
            startAt(value),
            endAt(`${value}\uf8ff`),
            limit(25)
        ));

        return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    };

    const searchAllUsers = async (term) => {
        const variants = uniqueSearchVariants(term);
        const userResults = new Map();

        if (!term.includes('/')) {
            const exactUserSnapshot = await getDocFromServer(doc(db, 'users', term));
            if (exactUserSnapshot.exists()) {
                userResults.set(exactUserSnapshot.id, { ...exactUserSnapshot.data(), id: exactUserSnapshot.id });
            }
        }

        const queryResults = await Promise.all([
            ...variants.map((variant) => runIdPrefixUserQuery(variant)),
            ...variants.map((variant) => runPrefixUserQuery('name', variant)),
            ...variants.map((variant) => runPrefixUserQuery('phone', variant))
        ]);

        queryResults.flat().forEach((user) => {
            if (matchesSearch(user, term)) {
                userResults.set(user.id, user);
            }
        });

        const users = Array.from(userResults.values()).slice(0, 50);
        return { users, userIds: users.map((user) => user.id), hasMore: false };
    };

    const searchBrandUsers = async (brandId, term) => {
        const clientIds = await fetchBrandClientIds(brandId);
        const brandUsers = await fetchUsersByIds(clientIds);
        const users = brandUsers.filter((user) => matchesSearch(user, term)).slice(0, 50);

        return { users, userIds: users.map((user) => user.id), hasMore: false };
    };

    const searchUsers = async (term) => {
        if (globalState.currentBrand && globalState.currentBrand !== 'All') {
            return await searchBrandUsers(globalState.currentBrand, term);
        }

        return await searchAllUsers(term);
    };

    // Fetch stamps for a specific user
    const fetchUserStamps = async (userId) => {
        const stampsQuery = query(collection(db, 'stamps'), where('userId', '==', userId));
        const stampsSnapshot = await getDocs(stampsQuery);
        return stampsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    };

    // Fetch brand data for stamps
    const fetchBrandsForStamps = async (stamps) => {
        const brandIds = Array.from(new Set(stamps.map(stamp => stamp.brandId)));
        const brands = await Promise.all(brandIds.map(async brandId => {
            const cachedBrand = globalState.brands?.find?.((brand) => brand.id === brandId);
            if (cachedBrand) {
                return cachedBrand;
            }

            const brandDoc = await getDocFromServer(doc(db, 'brands', brandId));
            return { ...brandDoc.data(), id: brandId };
        }));
        return stamps.map(stamp => ({
            ...stamp,
            brand: brands.find(brand => brand.id === stamp.brandId)
        }));
    };

    // Fetch rewards for a specific user
    const fetchUserRewards = async (userId) => {
        const rewardsQuery = query(collection(db, 'rewards'), where('userId', '==', userId));
        const rewardsSnapshot = await getDocs(rewardsQuery);
        return rewardsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
    };

    // Main fetch function that coordinates all data fetching
    const fetchLatestData = async (userFetchLimit, lastVisible) => {
        setLoading(true);
        setError('');
        try {
            // Fetch users and their IDs
            const { users, hasMore, allStamps } = await fetchUsers(userFetchLimit, lastVisible);
            setHasMore(hasMore);

            // Fetch stamps and rewards for each user
            const enrichedUsers = await Promise.all(users.map(async user => {
                // Filter stamps from already fetched stamps instead of making new query
                const userStamps = allStamps ?
                    allStamps.filter(stamp => stamp.userId === user.id) :
                    await fetchUserStamps(user.id);

                const stampsWithBrands = await fetchBrandsForStamps(userStamps);
                const rewards = await fetchUserRewards(user.id);
                const brandIds = Array.from(new Set(stampsWithBrands.map(stamp => stamp.brandId)));

                return {
                    ...user,
                    stamps: stampsWithBrands,
                    stampsCount: stampsWithBrands.length,
                    brands: brandIds
                        .map((brandId) => stampsWithBrands.find((stamp) => stamp.brandId === brandId)?.brand)
                        .filter(Boolean),
                    brandsCount: brandIds.length,
                    rewards,
                    rewardsCount: rewards.length
                };
            }));

            setLatestUsers(prev => [...prev, ...enrichedUsers]);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
            setHasMore(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Lollet Admin - Users</title>
            </Helmet>
            <Container size="xxl" className={styles.container}>
                <Stack gap="lg">
                    <Group justify="space-between" align="flex-end">
                        <div>
                            <Title order={1}>Users</Title>
                            <Text c="dimmed" mt={4}>
                                Search by user ID, name, or phone number and open a user to view stats.
                            </Text>
                        </div>
                        <TextInput
                            className={styles.search}
                            leftSection={<IconSearch size={16} />}
                            rightSection={searchInput ? (
                                <ActionIcon variant="subtle" size="sm" onClick={() => setSearchInput('')} aria-label="Clear search">
                                    <IconX size={14} />
                                </ActionIcon>
                            ) : null}
                            placeholder="Search users"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.currentTarget.value)}
                        />
                    </Group>

                    {error && <Text color="red" align="center">{error}</Text>}

                    <Paper withBorder radius="md" p="md">
                        {searchTerm && (
                            <Text fz="sm" c="dimmed" mb="md">
                                Showing search results for "{searchTerm}"
                            </Text>
                        )}

                        {loading ? (
                            <Center py="xl">
                                <Loader />
                            </Center>
                        ) : (
                            <>
                                <UsersTable data={latestUsers} loading={loading} error={error} />
                                {latestUsers.length === 0 && (
                                    <Text c="dimmed" ta="center" py="xl">
                                        No users found.
                                    </Text>
                                )}
                                {hasMore && !searchTerm && (
                                    <Group justify="center" mt="lg">
                                        <Button
                                            onClick={() => {
                                                setCurrentPage(currentPage + 1);
                                                fetchLatestData(fetchLimit, lastVisible);
                                            }}
                                        >
                                            Show More
                                        </Button>
                                    </Group>
                                )}
                            </>
                        )}
                    </Paper>
                </Stack>
            </Container>
        </>
    )
}
